/**
 * salesAttribution/{yearMonth} — 어시스트 빈 수납건의 팀 지정 저장소
 *
 * 일일보고서(dailyReports) 문서 안에 두지 않는 이유: 엑셀을 다시 올리면 그 날짜 문서가
 * 통째로 다시 써져 지정이 사라진다. 지정은 원장의 판단이므로 파이프라인이 덮어쓰지 못하는
 * 별도 문서에 둔다. 월 하나에 문서 하나.
 *
 *   receiptOverrides:  { "YYYY-MM-DD|차트번호": { team, by, at } }   ① 수납건 지정
 *   doctorTeamMapping: { "의사명": { team, by, at } }                 ② 의사 월 매핑 (그 달만)
 *   pendingRefresh:    지정 후 아직 실적 재계산을 안 돌린 상태
 *
 * 쓰기는 Firestore 규칙에서 admin 전용이다 (firestore.rules /salesAttribution).
 */
import { collection, doc, getDoc, getDocs, query, setDoc, where, deleteField } from 'firebase/firestore';
import { db } from '../../firebase';
import { attributionOverrideKey, type SalesAttributionDoc } from '../../utils/salesAttribution.ts';

const COLLECTION = 'salesAttribution';

export const loadSalesAttributionDoc = async (yearMonth: string): Promise<SalesAttributionDoc | null> => {
  const snap = await getDoc(doc(db, COLLECTION, yearMonth));
  return snap.exists() ? (snap.data() as SalesAttributionDoc) : null;
};

interface Actor { uid: string; email?: string | null }

const stamp = (actor: Actor) => ({ by: actor.email || actor.uid, at: new Date().toISOString() });

/** ① 수납건 지정. team이 '' 이면 지정 해제. */
export const setReceiptTeamOverride = async (
  yearMonth: string,
  date: string,
  chartNumber: string,
  team: string,
  actor: Actor
): Promise<void> => {
  const key = attributionOverrideKey(date, chartNumber);
  const ref = doc(db, COLLECTION, yearMonth);
  const patch: any = {
    pendingRefresh: true,
    updatedAt: new Date().toISOString(),
    receiptOverrides: { [key]: team ? { team, ...stamp(actor) } : deleteField() },
  };
  await setDoc(ref, patch, { merge: true });
};

/** ② 의사 월 매핑. team이 '' 이면 매핑 해제. */
export const setDoctorTeamMappingForMonth = async (
  yearMonth: string,
  doctorName: string,
  team: string,
  actor: Actor
): Promise<void> => {
  const ref = doc(db, COLLECTION, yearMonth);
  const patch: any = {
    pendingRefresh: true,
    updatedAt: new Date().toISOString(),
    doctorTeamMapping: { [doctorName]: team ? { team, ...stamp(actor) } : deleteField() },
  };
  await setDoc(ref, patch, { merge: true });
};

/** 실적 재계산을 돌린 뒤 호출 — '반영 안 된 지정' 표시를 끈다 */
export const markAttributionRefreshed = async (yearMonth: string): Promise<void> => {
  const ref = doc(db, COLLECTION, yearMonth);
  await setDoc(ref, { pendingRefresh: false, refreshedAt: new Date().toISOString() }, { merge: true });
};

/** 그 날짜 일일보고서의 집계행(teamCollection) — 화면이 서비스와 같은 규칙으로 풀을 판정하기 위해 읽는다 */
export const loadDailyTeamCollectionRows = async (date: string): Promise<any[]> => {
  const snap = await getDoc(doc(db, 'dailyReports', date));
  const rows = snap.exists() ? (snap.data() as any)?.teamCollection : null;
  return Array.isArray(rows) ? rows.map((r: any) => ({ ...r, docDate: date })) : [];
};

/**
 * 그 달 일일보고서에 등장한 진료의사 목록 — 의사 월 매핑은 월 단위이므로 목록도 월 단위여야 한다.
 * (그날 것만 모으면 다른 날짜에만 진료한 의사는 그 날짜로 이동해야만 매핑할 수 있다)
 */
export const loadMonthDoctors = async (yearMonth: string): Promise<string[]> => {
  const q = query(
    collection(db, 'dailyReports'),
    where('__name__', '>=', `${yearMonth}-01`),
    where('__name__', '<', `${yearMonth}-32`)
  );
  const snap = await getDocs(q);
  const names = new Set<string>();
  snap.docs.forEach((d) => {
    const items = (d.data() as any)?.receipts?.items;
    if (Array.isArray(items)) items.forEach((r: any) => {
      const n = (r?.doctorName ?? '').toString().trim();
      if (n) names.add(n);
    });
  });
  return [...names].sort();
};
